import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { devLog, devError } from '@/utils/devLog';

const ChooseRole = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract the 'from' state if present
  const from = location.state?.from as string | undefined;
  
  const handleSelectRole = async (role: 'student' | 'creator') => {
    setLoading(true);
    
    try {
      devLog(`[ChooseRole] Setting role to: ${role}, from state: ${from}`);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to select a role');
        // Updated to use login instead of student-auth
        navigate('/login', { state: { from } });
        return;
      }
      
      devLog(`[ChooseRole] Setting role for user ${user.id} to ${role}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role: role,
          updated_at: new Date().toISOString() 
        })
        .eq('id', user.id);
      
      if (error) {
        devError('[ChooseRole] Error setting role:', error);
        toast.error('Failed to set role: ' + error.message);
        return;
      }
      
      devLog(`[ChooseRole] Successfully set role to ${role}`);
      toast.success(`You are now registered as a ${role}!`);
      
      // Redirect based on role and from state
      if (role === 'student') {
        // Check if this was part of a course enrollment flow
        if (from?.startsWith('/') && from.split('/').length === 3) {
          // e.g. "/creatorX/courseY" - treat as course-enroll context
          const checkoutPath = `/checkout${from}`;
          devLog(`[ChooseRole] Student role with course path, redirecting to: ${checkoutPath}`);
          navigate(checkoutPath, { state: { from } });
        } else {
          // Regular student path
          devLog(`[ChooseRole] Student role, redirecting to dashboard`);
          navigate('/dashboard/student');
        }
      } else {
        // Creators need to set a username
        devLog(`[ChooseRole] Creator role, redirecting to username setup`);
        navigate('/onboarding/username');
      }
    } catch (error: any) {
      devError('[ChooseRole] Error:', error);
      toast.error('An error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-bold">Choose Your Role</CardTitle>
          <CardDescription className="text-center">
            Are you here to create courses or to learn?
            {from && <p className="mt-2 text-sm">You'll continue to your content after selecting a role.</p>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Button 
              onClick={() => handleSelectRole('creator')} 
              disabled={loading}
              className="h-32 flex flex-col items-center justify-center space-y-2"
              variant="outline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
                <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                <path d="M7 21h10"></path>
                <path d="M12 3v18"></path>
                <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"></path>
              </svg>
              <span>I'm a Creator</span>
            </Button>
            <Button 
              onClick={() => handleSelectRole('student')} 
              disabled={loading}
              className="h-32 flex flex-col items-center justify-center space-y-2"
              variant="outline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
                <path d="M12 9a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"></path>
                <path d="M2 8v8"></path>
                <path d="M22 8v8"></path>
                <path d="m7 3 10 18"></path>
                <path d="m7 21 10-18"></path>
              </svg>
              <span>I'm a Student</span>
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-gray-500">
            You can change your role later in your account settings.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ChooseRole;
